import React, { useState } from "react";
import { Link } from "@/lib/nav";
import { useTranslation } from "react-i18next";
import { Clock, ArrowRight, X, Crown, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { isOwner } from "@/lib/api";

export default function TrialBanner() {
  // v7.9.6 - IL 'GRATIS' NON ESISTE PIU'. Le prove gratuite sono chiuse alla radice
  // (la rotta che creava aziende dal nulla risponde 410) e i piani sono Basalt,
  // Obsidian, Vantablack. Questo cartello non ha piu' niente da dire: non torna a
  // parlare da solo se un domani un documento riportasse un `is_trial` vecchio.
  return null;
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [hidden, setHidden] = useState(false);
  if (!user || hidden || isOwner(user)) return null;

  const lic = user.license;
  const trial = user.trial;

  if (lic?.onprem) {
    return (
      <div data-testid="license-onprem-banner"
        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b lp-bd40 lp-b10 px-4 py-2.5 text-center text-sm text-[#9fc2ff]">
        <Crown className="h-4 w-4 shrink-0" />
        <span dangerouslySetInnerHTML={{ __html: t("trial.onprem") }} />
        <button onClick={() => setHidden(true)} data-testid="banner-dismiss" className="ml-1 opacity-60 hover:opacity-100" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  if (lic && (lic.active || lic.perpetual)) {
    return (
      <div data-testid="license-badge-founder"
        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-[#ffb020]/40 bg-gradient-to-r from-[#1a1305] via-[#221806] to-[#1a1305] px-4 py-2.5 text-center text-sm text-[#ffd98a]">
        <Award className="h-4 w-4 shrink-0 text-[#ffb020]" />
        <span><b>{t("trial.perpetualPre")}</b> · {lic.package_label}</span>
        <button onClick={() => setHidden(true)} data-testid="banner-dismiss" className="ml-1 opacity-60 hover:opacity-100" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  if (lic?.expired) {
    return (
      <div data-testid="license-expired-banner"
        className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b border-[#ff3333]/40 bg-[#ff3333]/10 px-4 py-2.5 text-center text-sm text-[#ff9a9a]">
        <Clock className="h-4 w-4 shrink-0" />
        <span>{t("trial.expiredPre")} <b>{lic.package_label}</b> {t("trial.expiredPost")}</span>
        <Link to="/license" data-testid="license-renew-cta" className="inline-flex items-center gap-1.5 border border-current px-3 py-1 font-mono text-[11px] uppercase tracking-wider hover:bg-white/10">
          {t("trial.renew")} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    );
  }

  if (lic && lic.days_left != null) {
    const d = lic.days_left;
    const urgent = d <= 7;
    return (
      <div data-testid="license-countdown"
        className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b px-4 py-2.5 text-center text-sm ${urgent ? "border-[#ff3333]/40 bg-[#ff3333]/10 text-[#ff9a9a]" : "lp-bd40 lp-b10 text-[#9fc2ff]"}`}>
        <Crown className="h-4 w-4 shrink-0" />
        <span data-testid="license-countdown-text">
          {t("trial.countdownPre")} <b>{lic.package_label}</b> ({lic.billing === "annual" ? t("trial.annual") : t("trial.monthly")}): <b>{d} {d === 1 ? t("trial.day") : t("trial.days")}</b> {t("trial.countdownMid")}
        </span>
        <Link to="/license" data-testid="license-renew-cta" className="inline-flex items-center gap-1.5 border border-current px-3 py-1 font-mono text-[11px] uppercase tracking-wider hover:bg-white/10">
          {t("trial.renew")} <ArrowRight className="h-3.5 w-3.5" />
        </Link>
        <button onClick={() => setHidden(true)} data-testid="banner-dismiss" className="ml-1 opacity-60 hover:opacity-100" aria-label="Close"><X className="h-4 w-4" /></button>
      </div>
    );
  }

  if (!trial?.is_trial) return null;
  const days = trial.days_left;
  const expired = days === 0;
  const dateLabel = trial.expires_at
    ? new Date(trial.expires_at).toLocaleDateString(i18n.language === "en" ? "en-GB" : "it-IT", { day: "2-digit", month: "long", year: "numeric" })
    : null;

  return (
    <div data-testid="trial-banner"
      className={`flex flex-wrap items-center justify-center gap-x-3 gap-y-1 border-b px-4 py-2.5 text-center text-sm ${expired ? "border-[#ff3333]/40 bg-[#ff3333]/10 text-[#ff9a9a]" : "border-[#ffb020]/40 bg-[#ffb020]/10 text-[#ffd28a]"}`}>
      <Clock className="h-4 w-4 shrink-0" />
      {expired ? (
        <span data-testid="trial-banner-text" dangerouslySetInnerHTML={{ __html: t("trial.trialExpired") }} />
      ) : (
        <span data-testid="trial-banner-text">
          {t("trial.trialLeftPre")} <b>{days} {days === 1 ? t("trial.day") : t("trial.days")}</b> {t("trial.trialLeftRemaining")}{dateLabel ? <> · {t("trial.trialExpiresOn")} {dateLabel}</> : null}.
        </span>
      )}
      <Link to="/license" data-testid="trial-banner-cta" className="inline-flex items-center gap-1.5 border border-current px-3 py-1 font-mono text-[11px] uppercase tracking-wider hover:bg-white/10">
        {t("trial.activateLicense")} <ArrowRight className="h-3.5 w-3.5" />
      </Link>
      <button onClick={() => setHidden(true)} data-testid="trial-banner-dismiss" className="ml-1 opacity-60 hover:opacity-100" aria-label="Close"><X className="h-4 w-4" /></button>
    </div>
  );
}
